import { ArrowUpRight, BookOpen, Clock } from "lucide-react";

export default function Blog({ posts, loading, error }) {
  const previewPosts = posts.slice(0, 3);

  return (
    <section className="journal-section" id="journal" aria-labelledby="journal-title">
      <div className="journal-section__heading">
        <div>
          <h2 id="journal-title">Notes from my desk</h2>
          <p>I use this space to make psychiatric ideas easier to approach—for patients, families, and fellow learners.</p>
        </div>
        <a className="journal-section__all" href="/notes">View all notes <ArrowUpRight size={17} aria-hidden="true" /></a>
      </div>

      {loading && !posts.length && (
        <div className="article-grid" aria-label="Loading articles">
          {[0, 1, 2].map((item) => <div className="article-skeleton" key={item} aria-hidden="true" />)}
          <span className="visually-hidden">Loading articles…</span>
        </div>
      )}

      {!loading && error && !previewPosts.length && <p className="content-message content-message--error">{error}</p>}

      {!loading && !error && !previewPosts.length && (
        <div className="journal-empty">
          <BookOpen size={28} aria-hidden="true" />
          <h3>The first note is taking shape.</h3>
          <p>I’m preparing a careful first piece. This notebook will grow one reviewed resource at a time.</p>
        </div>
      )}

      {!!previewPosts.length && (
        <div className="home-notes-grid">
          {previewPosts.map((post, index) => (
            <ArticleCard post={post} variant="home" showImage={index === 0} key={post.id} />
          ))}
        </div>
      )}
    </section>
  );
}

export function ArticleCard({ post, variant = "collection", showImage = true }) {
  return (
    <article className={`article-card article-card--${variant} article-card--tone-${safeTone(post.visualTone)}`} id={`post-${post.id}`}>
      {showImage && post.coverImage && (
        <img src={post.coverImage} alt={post.coverAlt || ""} width="800" height="520" loading="lazy" />
      )}
      <div className="article-card__body">
        <p className="article-card__meta">
          <span>{post.category || "Mental health"}</span>
          <span><Clock size={15} aria-hidden="true" /> {post.readingTime || "4 min"}</span>
        </p>
        <h3>{post.title}</h3>
        <p>{post.excerpt}</p>
        <a href={`/notes/${post.slug || post.id}`}>Read article <ArrowUpRight size={17} aria-hidden="true" /></a>
      </div>
    </article>
  );
}

export function ArticleBody({ body = "" }) {
  const blocks = body.split(/\n{2,}/).map((block) => block.trim()).filter(Boolean);
  return (
    <div className="article-body">
      {blocks.map((block, index) => {
        if (block.startsWith("## ")) return <h3 key={index}>{block.slice(3)}</h3>;
        if (block.startsWith("# ")) return <h3 key={index}>{block.slice(2)}</h3>;
        if (block === "---") return <hr key={index} />;
        if (block.startsWith(":: ")) return <aside className="article-callout" key={index}>{renderInlineLinks(block.slice(3))}</aside>;
        if (block.split("\n").every((line) => line.startsWith("> "))) {
          return <blockquote key={index}>{renderInlineLinks(block.split("\n").map((line) => line.slice(2)).join(" "))}</blockquote>;
        }
        const image = block.match(/^!\[([^\]]*)\]\(([^)]+)\)$/);
        if (image && isSafeImageUrl(image[2])) {
          return <figure className="article-inline-image" key={index}><img src={image[2]} alt={image[1]} loading="lazy" /><figcaption>{image[1]}</figcaption></figure>;
        }
        const embed = block.match(/^@\[(youtube|vimeo)\]\(([^)]+)\)$/i);
        const embedUrl = embed ? safeEmbedUrl(embed[1], embed[2]) : null;
        if (embedUrl) {
          return <figure className="article-embed" key={index}><iframe src={embedUrl} title={`${embed[1]} video`} loading="lazy" allow="accelerometer; autoplay; encrypted-media; picture-in-picture" allowFullScreen referrerPolicy="strict-origin-when-cross-origin" /></figure>;
        }
        if (block.split("\n").every((line) => line.startsWith("- "))) {
          return <ul key={index}>{block.split("\n").map((line) => <li key={line}>{line.slice(2)}</li>)}</ul>;
        }
        return <p key={index}>{renderInlineLinks(block)}</p>;
      })}
    </div>
  );
}

function safeTone(value) {
  const tones = new Set(["sage", "plum", "peach", "dusk", "blue", "walnut", "terracotta", "sand", "amber"]);
  return tones.has(value) ? value : "sage";
}

function isSafeImageUrl(value) {
  if (/^\/images\/[a-zA-Z0-9/_-]+\.(?:jpg|jpeg|png|webp|avif)$/i.test(value)) return true;
  try { return new URL(value).protocol === "https:"; } catch { return false; }
}

function safeEmbedUrl(provider, value) {
  try {
    const url = new URL(value);
    if (provider.toLowerCase() === "youtube") {
      const host = url.hostname.replace(/^www\./, "");
      const id = host === "youtu.be" ? url.pathname.slice(1) : (host === "youtube.com" ? url.searchParams.get("v") : "");
      return /^[a-zA-Z0-9_-]{6,20}$/.test(id || "") ? `https://www.youtube-nocookie.com/embed/${id}` : null;
    }
    if (provider.toLowerCase() === "vimeo" && ["vimeo.com", "www.vimeo.com"].includes(url.hostname)) {
      const id = url.pathname.split("/").filter(Boolean)[0];
      return /^\d{6,12}$/.test(id || "") ? `https://player.vimeo.com/video/${id}` : null;
    }
  } catch {
    return null;
  }
  return null;
}

function renderInlineLinks(text) {
  const pattern = /\[([^\]]+)\]\((https:\/\/[^)]+)\)/g;
  const parts = [];
  let cursor = 0;
  for (const match of text.matchAll(pattern)) {
    parts.push(text.slice(cursor, match.index));
    parts.push(<a href={match[2]} target="_blank" rel="noreferrer" key={`${match.index}-${match[2]}`}>{match[1]} <ArrowUpRight size={14} aria-hidden="true" /></a>);
    cursor = match.index + match[0].length;
  }
  parts.push(text.slice(cursor));
  return parts;
}
